import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { GameConfig } from '../config/GameConfig';

export class CatCollarWeapon {
  private scene: Phaser.Scene;
  private player: Player;
  private enemies: Phaser.Physics.Arcade.Group;
  private damageMultiplier: number = 1;
  private attackTimer?: Phaser.Time.TimerEvent;
  private auraRadius: number = GameConfig.WEAPON_DATA.CAT_COLLAR.RADIUS;
  private auraGraphics?: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, player: Player, enemies: Phaser.Physics.Arcade.Group) {
    this.scene = scene;
    this.player = player;
    this.enemies = enemies;

    this.createAuraGraphics();
    this.startAura();
  }

  private createAuraGraphics(): void {
    this.auraGraphics = this.scene.add.graphics();
    this.auraGraphics.setDepth(4);
  }

  private startAura(): void {
    this.attackTimer = this.scene.time.addEvent({
      delay: GameConfig.WEAPON_DATA.CAT_COLLAR.TICK_RATE, // Damage every second
      callback: this.pulseAura,
      callbackScope: this,
      loop: true
    });

    // Visual update less frequently
    this.scene.time.addEvent({
      delay: 100,
      callback: this.updateVisuals,
      callbackScope: this,
      loop: true
    });
  }

  private updateVisuals(): void {
    if (!this.auraGraphics || !this.player.active) return;

    this.auraGraphics.clear();

    // Draw pulsing aura ring
    const pulse = Math.sin(Date.now() / 200) * 0.3 + 0.7;
    this.auraGraphics.lineStyle(3, 0xFFD700, 0.2 * pulse);
    this.auraGraphics.strokeCircle(this.player.x, this.player.y, this.auraRadius);

    this.auraGraphics.lineStyle(2, 0xFFF8DC, 0.3 * pulse);
    this.auraGraphics.strokeCircle(this.player.x, this.player.y, this.auraRadius - 5);
  }

  private pulseAura(): void {
    if (!this.player.active) return;

    // Damage all enemies in radius
    const enemyArray = this.enemies.getChildren() as Enemy[];
    enemyArray.forEach(enemy => {
      if (!enemy.active) return;

      const distance = Phaser.Math.Distance.Between(
        this.player.x, this.player.y,
        enemy.x, enemy.y
      );

      if (distance < this.auraRadius) {
        enemy.takeDamage(GameConfig.WEAPON_DATA.CAT_COLLAR.DAMAGE * this.damageMultiplier);
      }
    });

    // Flash effect on pulse
    if (this.auraGraphics) {
      this.scene.tweens.add({
        targets: this.auraGraphics,
        alpha: { from: 0.8, to: 0.3 },
        duration: 300,
        ease: 'Quad.easeOut'
      });
    }
  }

  public updateDamageMultiplier(multiplier: number): void {
    this.damageMultiplier = multiplier;
  }

  public upgradeDamage(amount: number): void {
    this.damageMultiplier += amount;
  }

  public upgradeRadius(amount: number): void {
    this.auraRadius += amount;
  }

  public destroy(): void {
    if (this.attackTimer) {
      this.attackTimer.destroy();
    }
    if (this.auraGraphics) {
      this.auraGraphics.destroy();
    }
  }
}
